// -- Global imports --
import parsePhoneNumber from 'libphonenumber-js/max';

// -- Framework imports --
import type { App, Plugin } from 'vue';

/**
 * Parse a phone number, validate it and return its proper string representation
 * @param number Phone number to parse
 * @param prefix_length Prefix length of the trunk an internal number might be assigned to
 */
function getParsedOrInternalNumber(number: string, prefix_length?: number): string | null {
	if (prefix_length && isValidInternalNumber(number, prefix_length)) {
		return number;
	}

	const parsedNumber = parsePhoneNumber(number, 'DE');
	return !parsedNumber?.isValid() ? null : parsedNumber.format('E.164');
}

/**
 * Validate that a given number is a valid internal number
 * @param number Phone number to validate
 * @param prefix_length Prefix length of the trunk the number is assigned to, set to 0 to disable length check
 */
function isValidInternalNumber(number: string, prefix_length: number): boolean {
	return prefix_length === 0 || number.length <= prefix_length ? !!number.match('^[0-9*#+]+$') : false;
}

/**
 * Validate that a given number is a valid public phone number
 * @param number
 */
function isValidPublicNumber(number: string): boolean {
	return !!parsePhoneNumber(number, 'DE')?.isValid();
}

/**
 * Format a phone number if possible or return the input string unaltered
 * @param number Phone number to format
 */
function formatNumber(number: string): string {
	const parsedNumber = parsePhoneNumber(number, 'DE');
	return parsedNumber?.isValid() ? parsedNumber.formatInternational() : number;
}

// -- Global Asterisk interface available in app --
const $phone_numbers = {
	isValidOrInternalNumber: (number: string, prefix_length?: number): boolean =>
		getParsedOrInternalNumber(number, prefix_length) !== null,
	getParsedOrInternalNumber: getParsedOrInternalNumber,
	isValidInternalNumber: isValidInternalNumber,
	isValidPublicNumber: isValidPublicNumber,
	formatNumber: formatNumber,
};

// -- Define plugin --
const PhoneNumberPlugin: Plugin = {
	install: (app: App) => {
		app.config.globalProperties.$phone_numbers = $phone_numbers;
	},
};

// -- Exports --
export default PhoneNumberPlugin;

export { $phone_numbers };

declare module '@vue/runtime-core' {
	export interface ComponentCustomProperties {
		$phone_numbers: typeof $phone_numbers;
	}
}
